import { isBugBountyParticipant } from '@atlassian/amkt-common';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import { EyebrowSmall, H3 } from '@atlassian/mpac-primitives';
import { AtlassianProductHostingType } from '@atlassian/mpac-types/graphql-gateway-types';
import { useSource } from '@atlassian/mpac-utils';
import React from 'react';
import { TEST_IDS } from '../../primitives/testIds';
import { HeaderPartnerDetails } from '../HeaderPartnerDetails';
import { InstallCount, useInstallMessage } from '../InstallCount';
import * as utils from './AppHeader.utils';
import { AppDetailsContainer, CompatibilitiesContainer, HeaderContainerEMCEE, HeaderContainerMPAC, HeaderTopRowContainer, IconContainer, ItemValue, SocialProofHeading, TrustSignalsContainer, TrustSignalsItem } from './AppHeaderV2.styles';
import HeaderAppIcon, { getAppIcon } from './components/HeaderAppIcon';
import HeaderCertificationBadge from './components/HeaderCertificationBadge';
import HeaderLozenges from './components/HeaderLozenges';
import HeaderRatings from './components/HeaderRatings';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// eslint-disable-next-line max-lines-per-function, complexity
function AppHeaderV2(_ref) {
  var _appHeaderDetails$pro, _appHeaderDetails$pro2, _appHeaderDetails$com;
  let {
    appHeaderDetails,
    appIconLink,
    appIconSize = 'large',
    appRatingLink,
    compatibilitySummary = null,
    headerInjection,
    isAppCloudFortified = false,
    lozenges = [],
    openMarketplaceLinksInNewTab = true,
    showInstallationCount = false,
    bugBountyParticipant = null,
    hostingSelect,
    onTabChange
  } = _ref;
  const {
    enableRunsOnAtlassian,
    enableAppificationChanges
  } = useStatsig();
  const isRunsOnAtlassianApp = enableRunsOnAtlassian() && (((_appHeaderDetails$pro = appHeaderDetails.programs) === null || _appHeaderDetails$pro === void 0 ? void 0 : (_appHeaderDetails$pro2 = _appHeaderDetails$pro.runsOnAtlassian) === null || _appHeaderDetails$pro2 === void 0 ? void 0 : _appHeaderDetails$pro2.status) || false);
  const showNewAppMessage = utils.showNewAppMessage(appHeaderDetails);
  const showSecurityBadge = isAppCloudFortified || isBugBountyParticipant(AtlassianProductHostingType.Cloud, bugBountyParticipant) || isRunsOnAtlassianApp;
  const source = useSource();
  const isMPAC = source === 'MPAC';
  const {
    tooltipMessage: installTooltipMessage,
    installCountMessage,
    showDownloadIcon,
    isLoading: isInstallMessageLoading,
    title: installCountTitle
  } = useInstallMessage({
    distribution: appHeaderDetails.distribution,
    showNewAppMessage,
    compatibleProducts: (_appHeaderDetails$com = appHeaderDetails.compatibleProducts) !== null && _appHeaderDetails$com !== void 0 ? _appHeaderDetails$com : [],
    enableAppificationChanges: enableAppificationChanges(),
    showAetherAppListingPage: true
  });
  const HeaderContainer = isMPAC ? HeaderContainerMPAC : HeaderContainerEMCEE;
  return /*#__PURE__*/_jsxs(HeaderContainer, {
    "data-testid": TEST_IDS.APP_HEADER,
    children: [/*#__PURE__*/_jsxs("div", {
      style: {
        width: '100%'
      },
      children: [/*#__PURE__*/_jsxs(HeaderTopRowContainer, {
        source: source,
        children: [/*#__PURE__*/_jsxs(AppDetailsContainer, {
          children: [/*#__PURE__*/_jsx(IconContainer, {
            "data-testid": TEST_IDS.APP_HEADER_ICON,
            children: /*#__PURE__*/_jsx(HeaderAppIcon, {
              size: appIconSize,
              origin: appHeaderDetails.origin,
              icon: appHeaderDetails.icon,
              appKey: appHeaderDetails.key,
              appIconLink: appIconLink
            })
          }), /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(H3, {
              as: "h1",
              "data-testid": TEST_IDS.APP_HEADER_APP_NAME,
              children: appHeaderDetails.name
            }), /*#__PURE__*/_jsx(HeaderPartnerDetails, {
              name: appHeaderDetails.partner.name,
              link: appHeaderDetails.partner.link,
              partnerTierType: appHeaderDetails.partner.partnerTierType
            }), compatibilitySummary && /*#__PURE__*/_jsx(CompatibilitiesContainer, {
              "data-testid": TEST_IDS.APP_HEADER_COMPATIBILITIES,
              children: compatibilitySummary
            })]
          })]
        }), !!headerInjection && /*#__PURE__*/_jsx("div", {
          "data-testid": TEST_IDS.APP_HEADER_INJECTION,
          children: headerInjection
        })]
      }), /*#__PURE__*/_jsxs(TrustSignalsContainer, {
        children: [!showNewAppMessage && /*#__PURE__*/_jsxs(TrustSignalsItem, {
          width: "13.75rem",
          "data-testid": TEST_IDS.APP_HEADER_RATINGS,
          children: [/*#__PURE__*/_jsx(SocialProofHeading, {
            children: /*#__PURE__*/_jsx(EyebrowSmall, {
              children: "OVERALL RATINGS"
            })
          }), /*#__PURE__*/_jsx(ItemValue, {
            children: /*#__PURE__*/_jsx(HeaderRatings, {
              appKey: appHeaderDetails.key,
              appRatingLink: appRatingLink,
              openMarketplaceLinksInNewTab: openMarketplaceLinksInNewTab,
              ratings: appHeaderDetails.ratings,
              onTabChange: onTabChange
            })
          })]
        }), showInstallationCount && !!installCountMessage && /*#__PURE__*/_jsxs(TrustSignalsItem, {
          minWidth: "7.5rem",
          width: "unset",
          tabIndex: 0,
          "data-testid": TEST_IDS.APP_HEADER_INSTALL_COUNT,
          "aria-label": installTooltipMessage || '',
          children: [/*#__PURE__*/_jsx(SocialProofHeading, {
            children: /*#__PURE__*/_jsx(EyebrowSmall, {
              children: installCountTitle || 'INSTALLS'
            })
          }), /*#__PURE__*/_jsx(ItemValue, {
            children: /*#__PURE__*/_jsx(InstallCount, {
              tooltipMessage: installTooltipMessage,
              installCountMessage: installCountMessage,
              showDownloadIcon: showDownloadIcon,
              isLoading: isInstallMessageLoading,
              showAetherAppListingPage: true
            })
          })]
        }), !!lozenges.length && /*#__PURE__*/_jsxs(TrustSignalsItem, {
          width: showSecurityBadge ? '13.75rem' : '12.5rem',
          children: [/*#__PURE__*/_jsx(SocialProofHeading, {
            children: /*#__PURE__*/_jsx(EyebrowSmall, {
              children: "SUPPORT"
            })
          }), /*#__PURE__*/_jsx(ItemValue, {
            children: /*#__PURE__*/_jsx(HeaderLozenges, {
              lozenges: lozenges
            })
          })]
        }), showSecurityBadge && /*#__PURE__*/_jsxs(TrustSignalsItem, {
          "data-testid": TEST_IDS.APP_HEADER_CERTIFICATION,
          children: [/*#__PURE__*/_jsx(SocialProofHeading, {
            children: /*#__PURE__*/_jsx(EyebrowSmall, {
              children: "TRUST SIGNALS"
            })
          }), /*#__PURE__*/_jsx(ItemValue, {
            children: /*#__PURE__*/_jsx(HeaderCertificationBadge, {
              isRunsOnAtlassianApp: isRunsOnAtlassianApp,
              isAppCloudFortified: isAppCloudFortified,
              bugBountyParticipant: bugBountyParticipant
            })
          })]
        })]
      })]
    }), hostingSelect]
  });
}
AppHeaderV2.getAppIcon = getAppIcon;
export default AppHeaderV2;